/* Sentai app reels. On sections that turn them on, the app screens in the feature slider are
   live HTML (snippets/st-app-reel.liquid), and a short scripted reel plays on the selected one
   while the section is on screen, in the manner of a product reel: text writes itself, figures
   count up, a finger taps through the app. Timings and states: docs/APP_SLIDER.md.

   st-theme.js's AppTabs stays the one owner of the selection. It calls reels.select() when the
   selection changes and reels call tabs.selectFeature() to move on by themselves. Each reel is
   an async function; cancelling it throws away its screen and mounts a fresh copy, so a reel
   always starts from the markup. With reduced motion, a reel runs to its end at once and
   stops on its Figma screen, and nothing advances by itself. */
(() => {
  'use strict';
  if (window.SentaiAppReels) return;
  const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reducedMotion = () => motionPreference.matches;
  const EASE_OUT = 'cubic-bezier(.22,1,.36,1)';
  const SPRING = 'cubic-bezier(.34,1.45,.64,1)';
  const HOLD_MS = 2200;          // a finished screen stays readable before the next reel
  const IMAGE_MS = 6000;         // a screenshot panel in an auto-advancing slider

  class Cancelled extends Error {}

  // Reel helpers. `mode` is 'play'; 'end', where every step jumps to its end, for a reel a
  // visitor has stopped; or 'still', which also jumps but leaves each screen where Figma shows
  // it, for reduced motion.
  const director = (screen, signal, mode) => {
    const instant = mode !== 'play';
    const still = mode === 'still';
    const guard = () => { if (signal.aborted) throw new Cancelled(); };
    const finished = animation => animation.finished.then(() => guard(), () => { throw new Cancelled(); });
    // Steps a reel starts without awaiting must not report their cancellation as an error.
    const soft = promise => { promise.catch(() => {}); return promise; };
    const wait = ms => new Promise((resolve, reject) => {
      if (signal.aborted) return reject(new Cancelled());
      if (instant || ms <= 0) return resolve();
      const timer = setTimeout(resolve, ms);
      signal.addEventListener('abort', () => { clearTimeout(timer); reject(new Cancelled()); }, { once: true });
    });
    const $ = selector => screen.querySelector(selector);
    const $$ = selector => [...screen.querySelectorAll(selector)];
    const list = target => (target == null ? [] : typeof target === 'string' ? $$(target) : target instanceof Element ? [target] : [...target]).filter(Boolean);
    const play = (target, keyframes, options = {}) => {
      guard();
      // A still screen needs no animation: every reel ends where its markup's own styles stand.
      if (instant) return Promise.resolve();
      const animations = list(target).map((element, i) => element.animate(keyframes, {
        fill: 'both', easing: EASE_OUT, ...options,
        duration: options.duration ?? 500,
        delay: (options.delay ?? 0) + i * (options.stagger ?? 0)
      }));
      return soft(Promise.all(animations.map(finished)));
    };
    // Content fades in as it rises. Nothing blurs: a filter, even at blur(0), puts text in a
    // layer of its own that some browsers draw soft under the page's zoom.
    const reveal = (target, options = {}) => play(target, [
      { opacity: 0, transform: `translateY(${options.y ?? 10}px) scale(${options.scale ?? 1})` },
      { opacity: 1, transform: 'none' }
    ], { duration: 600, ...options });
    const pop = (target, options = {}) => play(target, [
      { opacity: 0, transform: 'scale(.6)' }, { opacity: 1, transform: 'none' }
    ], { duration: 520, easing: SPRING, ...options });
    // Characters arrive at a typing pace, with a caret. The whole text is laid out from the
    // start, its untyped part transparent, so lines never re-wrap and nothing below moves.
    const type = (element, text, options = {}) => soft((async () => {
      guard();
      element.textContent = text;
      if (instant) return;
      const typed = document.createTextNode('');
      const caret = document.createElement('span');
      caret.className = 'st-ui-caret';
      const rest = document.createElement('span');
      rest.className = 'st-ui-type-rest';
      rest.textContent = text;
      element.replaceChildren(typed, caret, rest);
      for (let i = 0; i < text.length; i++) {
        typed.data = text.slice(0, i + 1);
        rest.textContent = text.slice(i + 1);
        await wait((options.speed ?? 38) * (/[,.?!]/.test(text[i]) ? 4 : 1) * (0.6 + Math.random() * 0.8));
      }
      element.textContent = text;
    })());
    // AI text writes itself word by word, each word fading in.
    const stream = async (element, options = {}) => {
      guard();
      const words = element.textContent.trim().split(/\s+/);
      element.textContent = '';
      const spans = words.map(word => {
        const span = document.createElement('span');
        span.className = 'st-ui-word';
        span.textContent = word + ' ';
        element.append(span);
        return span;
      });
      await play(spans, [{ opacity: 0 }, { opacity: 1 }],
        { duration: 380, stagger: options.stagger ?? 45, easing: 'ease-out' });
      // The words go back to plain text, as the markup had it.
      element.textContent = words.join(' ');
    };
    // Figures count up to the value the markup shows (a number, or a time such as 8:21).
    const count = (element, options = {}) => {
      guard();
      const target = element.textContent.trim();
      const [whole, minutes] = target.split(':');
      if (instant) return Promise.resolve();
      const duration = options.duration ?? 900;
      return soft(new Promise((resolve, reject) => {
        const start = performance.now();
        const frame = now => {
          if (signal.aborted) return reject(new Cancelled());
          const t = Math.min(1, (now - start) / duration);
          const e = 1 - Math.pow(1 - t, 3);
          if (minutes !== undefined) {
            const total = Math.round((+whole * 60 + +minutes) * e);
            element.textContent = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
          } else element.textContent = String(Math.round(+whole * e));
          if (t < 1) requestAnimationFrame(frame); else { element.textContent = target; resolve(); }
        };
        requestAnimationFrame(frame);
      }));
    };
    // A finger tap: the marker glides to the control, presses, and a ripple spreads. Positions
    // are fractions of the screen, so they hold at any size and under the page's zoom.
    const finger = screen.closest('.st-phone')?.querySelector('.st-phone__finger');
    const tap = async (target, options = {}) => {
      guard();
      const element = typeof target === 'string' ? $(target) : target;
      if (instant || !finger) return;
      const box = screen.getBoundingClientRect();
      const r = element.getBoundingClientRect();
      const x = ((r.left + r.width * (options.x ?? .5)) - box.left) / box.width * 100;
      const y = ((r.top + r.height * (options.y ?? .5)) - box.top) / box.height * 100;
      const from = finger.dataset.at ? JSON.parse(finger.dataset.at) : null;
      finger.dataset.at = JSON.stringify([x, y]);
      if (!from) {
        finger.style.left = `${x}%`; finger.style.top = `${y}%`;
        await play(finger, [{ opacity: 0, transform: 'translate(-50%,-50%) scale(1.6)' }, { opacity: 1, transform: 'translate(-50%,-50%) scale(1)' }], { duration: 380 });
      } else {
        await play(finger, [{ left: `${from[0]}%`, top: `${from[1]}%` }, { left: `${x}%`, top: `${y}%` }], { duration: 650, easing: 'cubic-bezier(.65,0,.35,1)' });
        finger.style.left = `${x}%`; finger.style.top = `${y}%`;
      }
      const ripple = document.createElement('span');
      ripple.className = 'st-phone__ripple';
      ripple.style.left = `${x}%`; ripple.style.top = `${y}%`;
      finger.after(ripple);
      play(ripple, [{ opacity: .55, transform: 'translate(-50%,-50%) scale(.4)' }, { opacity: 0, transform: 'translate(-50%,-50%) scale(2.4)' }], { duration: 600, easing: 'ease-out' })
        .then(() => ripple.remove(), () => ripple.remove());
      await Promise.all([
        play(finger, [{ transform: 'translate(-50%,-50%) scale(1)' }, { transform: 'translate(-50%,-50%) scale(.78)' }, { transform: 'translate(-50%,-50%) scale(1)' }], { duration: 300, easing: 'ease-in-out' }),
        play(element, [{ transform: 'none' }, { transform: `scale(${options.press ?? .96})` }, { transform: 'none' }], { duration: 300, easing: 'ease-in-out', fill: 'none' })
      ]);
    };
    const lift = () => soft((async () => {
      if (instant || !finger || !finger.dataset.at) return;
      await play(finger, [{ opacity: 1 }, { opacity: 0 }], { duration: 300 });
      delete finger.dataset.at;
    })());
    // Scroll a screen's content smoothly. The still view keeps it where Figma shows it.
    const scrollTo = (element, top, options = {}) => {
      guard();
      if (still) return Promise.resolve();
      element.classList.add('is-scrolled');
      if (instant) { element.scrollTop = top; return Promise.resolve(); }
      const from = element.scrollTop;
      const duration = options.duration ?? 700;
      return soft(new Promise((resolve, reject) => {
        const start = performance.now();
        const frame = now => {
          if (signal.aborted) return reject(new Cancelled());
          const t = Math.min(1, (now - start) / duration);
          const e = t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
          element.scrollTop = from + (top - from) * e;
          if (t < 1) requestAnimationFrame(frame); else resolve();
        };
        requestAnimationFrame(frame);
      }));
    };
    // iOS-style navigation between two full-screen pages of the same screen.
    const navigate = async (from, to, { back = false } = {}) => {
      guard();
      to.hidden = false;
      if (instant) { from.hidden = true; return; }
      const options = { duration: 520, easing: 'cubic-bezier(.32,.72,0,1)', fill: 'none' };
      from.style.zIndex = back ? 1 : 0;
      to.style.zIndex = back ? 0 : 1;
      const under = [{ transform: 'translateX(-30%)', filter: 'brightness(.6)' }, { transform: 'none', filter: 'none' }];
      const over = [{ transform: 'translateX(100%)' }, { transform: 'none' }];
      await Promise.all(back
        ? [play(to, under, options), play(from, [...over].reverse(), options)]
        : [play(to, over, options), play(from, [...under].reverse(), options)]);
      from.hidden = true;
      from.style.zIndex = to.style.zIndex = '';
    };
    // Where an element sits in a scroll container, in the container's own CSS pixels: the
    // page's zoom scales client rectangles but not scrollTop.
    const offset = (element, container) => {
      const box = container.getBoundingClientRect();
      const scale = box.height ? container.offsetHeight / box.height : 1;
      return (element.getBoundingClientRect().top - box.top) * scale + container.scrollTop;
    };
    // One iPhone point in CSS pixels, for reels that measure the screen.
    const pt = screen.offsetWidth / 390 || 1;
    return { screen, $, $$, wait, play, reveal, pop, type, stream, count, tap, lift, scrollTo, navigate, offset, instant, still, pt };
  };

  // The reels, one per screen in snippets/st-app-reel.liquid. Each moves between real app
  // states, and its still (reduced-motion) view is the Figma screen itself. Only the top
  // ~490pt of the phone shows in the Home layout, so the action stays up there.
  const reels = (() => {
  const twinkle = (d, icon) => d.play(icon, [
    { transform: 'scale(.4) rotate(-40deg)', opacity: .3 },
    { transform: 'scale(1.25) rotate(12deg)', opacity: 1, offset: .6 },
    { transform: 'none', opacity: 1 }
  ], { duration: 900, easing: 'ease-out' });

  // The AI summary card: the sparkle twinkles, then the text writes itself in. There are no
  // placeholder lines: bars that came and went before the words read as a flash.
  const summarise = async (d, card, textSelector, moreSelector) => {
    const text = card.querySelector(textSelector);
    await twinkle(d, card.querySelector('.st-ui-ic'));
    await d.reveal(text, { duration: 1, y: 0 });
    await d.stream(text, { stagger: 38 });
    await d.reveal(card.querySelector(moreSelector), { duration: 400 });
  };

  // Typing into an outlined text field: focus, the label floats, characters arrive.
  const fill = async (d, field, value) => {
    await d.tap(field, { x: .3 });
    field.classList.add('is-focused');
    await d.wait(200);
    await d.type(field.querySelector('.st-ui-field__value'), value, { speed: 70 });
    field.classList.remove('is-focused');
    field.classList.add('is-filled');
  };

  // Activity -Dash 7 days: the week's summary, its key stats, then the Wellbeing trend.
  const insights = {
    length: 10000,
    async play(d) {
      const card = d.$('.st-ui-card');
      const rows = d.$$('.st-ui-stat');
      const dash = d.$('.st-ui-dash');
      const wellbeing = d.$('.st-ui-card--wellbeing');
      const grid = wellbeing.querySelector('.st-ui-wb__grid');
      const line = wellbeing.querySelector('.st-ui-wb__line');
      const later = [wellbeing.querySelector('.st-ui-wb__area'), wellbeing.querySelector('.st-ui-wb__avg'), wellbeing.querySelector('.st-ui-wb__avg-label')];
      const values = [...wellbeing.querySelectorAll('.st-ui-wb__values text')];
      const chip = wellbeing.querySelector('.st-ui-chip');
      // The chart waits undrawn below the crop until the screen moves down to it.
      if (!d.instant) {
        line.style.strokeDasharray = '1';
        line.style.strokeDashoffset = '1';
        [grid, ...later, ...values, chip].forEach(element => { element.style.opacity = 0; });
      }
      await d.wait(400);
      await summarise(d, card, '.st-ui-card__text', '.st-ui-card__more');
      await d.reveal(d.$('.st-ui-card__section'), { duration: 400 });
      d.reveal(rows, { stagger: 140 });
      await Promise.all(rows.map((row, i) => d.wait(i * 140).then(() => Promise.all([
        d.count(row.querySelector('.st-ui-num'), { duration: 1000 }),
        d.pop(row.querySelector('.st-ui-stat__trend'), { delay: 700 })
      ]))));
      if (d.still) return;   // the still view is the Figma "Dash 7 days" screen
      await d.wait(600);
      // The card comes up to just under the date selector, so the whole chart, day labels
      // included, ends above the ~493pt the Home crop shows on desktops and phones.
      await d.scrollTo(dash, d.offset(wellbeing, dash) - 5 * d.pt, { duration: 1300 });
      await d.play(grid, [{ opacity: 0 }, { opacity: 1 }], { duration: 400 });
      await d.play(line, [{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], { duration: 1500, easing: 'cubic-bezier(.45,0,.2,1)' });
      d.play(later, [{ opacity: 0 }, { opacity: 1 }], { duration: 500, stagger: 120 });
      await d.pop(values, { stagger: 90, duration: 420 });
      await d.play(chip, [{ opacity: 0, transform: 'scale(.6)' }, { opacity: .8, transform: 'none' }], { duration: 450, easing: SPRING });
    }
  };

  const chat = {
    length: 9000,
    async play(d) {
      const main = d.$('.st-ui-chat-main');
      const sent = d.$('.st-ui-msg--new .st-ui-msg__bubble');
      const reply = d.$('.st-ui-msg--reply .st-ui-msg__bubble');
      const words = sent.textContent;
      await d.wait(500);
      // The new day scrolls into view.
      const date = d.$$('.st-ui-chat-date')[1];
      await Promise.all([
        d.scrollTo(main, d.offset(date, main) - 18 * d.pt, { duration: 900 }),
        d.reveal(date, { delay: 300 })
      ]);
      // You type...
      const typing = d.type(sent, words, { speed: 26 });
      await d.reveal(sent, { duration: 350, y: 8, scale: .97 });
      await typing;
      await d.play(sent, [{ transform: 'none' }, { transform: 'scale(1.02)' }, { transform: 'none' }], { duration: 380, easing: SPRING });
      await d.reveal(sent.nextElementSibling, { duration: 400 });
      await d.wait(700);
      // ...they talk: Becky's spoken reply arrives as a text message.
      await d.wait(600);
      await d.reveal(d.$('.st-ui-msg--reply .st-ui-msg__from'), { duration: 400 });
      await d.reveal(reply, { y: 16, scale: .96, duration: 600, easing: SPRING });
      await d.reveal(reply.nextElementSibling, { duration: 400 });
    }
  };

  // Activity - Log: the morning's summary, then the log one entry at a time. Each reply
  // arrives as its bubble pops in.
  const morning = {
    length: 10500,
    async play(d) {
      const main = d.$('.st-ui-log-main');
      const items = d.$$('.st-ui-log-item');
      await d.wait(400);
      await summarise(d, d.$('.st-ui-ai-card'), '.st-ui-ai-card__text', '.st-ui-ai-card__more');
      await d.wait(400);
      d.reveal([d.$('.st-ui-subheading'), d.$('.st-ui-log-card')], { stagger: 120, duration: 450 });
      await d.scrollTo(main, d.offset(d.$('.st-ui-subheading'), main) - 4 * d.pt, { duration: 1000 });
      for (const [i, item] of items.entries()) {
        if (i) await d.scrollTo(main, d.offset(item, main) - 12 * d.pt, { duration: 1000 });
        await d.reveal(item.querySelector('.st-ui-log-item__head'), { y: 8, duration: 400 });
        await d.play(item.querySelectorAll('.st-ui-bubble'), [
          { opacity: 0, transform: 'translateY(10px) scale(.8)' }, { opacity: 1, transform: 'none' }
        ], { duration: 550, stagger: 450, easing: SPRING });
        await d.wait(500);
      }
    }
  };

  const family = {
    length: 13000,
    async play(d) {
      const list = d.$('.st-ui-page--list');
      const add = d.$('.st-ui-page--add');
      const sylvia = d.$('.st-ui-member--new');
      sylvia.hidden = true;   // joins the list once saved
      await d.wait(300);
      await d.reveal(d.$$('.st-ui-member').slice(0, 2), { stagger: 160, y: 16 });
      if (d.still) return;   // the still view is the Figma "Family & friends access" screen
      await d.wait(700);
      // + opens "Add member".
      await d.tap(d.$('.st-ui-fab'));
      d.lift();
      await d.navigate(list, add);
      await fill(d, add.querySelector('[data-st-field="first"]'), 'Sylvia');
      await fill(d, add.querySelector('[data-st-field="last"]'), 'Martin');
      await fill(d, add.querySelector('[data-st-field="relationship"]'), 'Neighbour');
      // App access: what Sylvia can see. The heading comes up towards the top, as far as the
      // form scrolls, so the switches sit inside the ~493pt the Home crop shows.
      const form = add.querySelector('.st-ui-settings-main');
      await d.scrollTo(form, Math.min(d.offset(add.querySelector('.st-ui-access'), form) - 16 * d.pt, form.scrollHeight - form.clientHeight), { duration: 1100 });
      for (const name of ['shopping', 'chat']) {
        const toggle = add.querySelector(`[data-st-toggle="${name}"]`);
        await d.tap(toggle);
        toggle.classList.add('is-on');
        await d.wait(350);
      }
      await d.tap(add.querySelector('.st-ui-save-button'));
      d.lift();
      sylvia.hidden = false;
      sylvia.style.opacity = 0;
      await d.navigate(add, list, { back: true });
      sylvia.style.opacity = '';
      await d.reveal(sylvia, { y: 24, scale: .96, duration: 700, easing: SPRING });
      await d.pop(sylvia.querySelector('.st-ui-member__status .st-ui-ic'), { duration: 500 });
    }
  };

    return { insights, chat, morning, family };
  })();

  class AppReels {
    constructor(tabs) {
      this.tabs = tabs;
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.panels = [...tabs.querySelectorAll('[data-st-panel]')];
      this.buttons = [...tabs.querySelectorAll('[data-st-tab]')];
      // Each reel starts from a fresh copy of its screen as the page delivered it. The copies are
      // kept in a template's inert document: a detached copy in the page fetches its lazy
      // pictures at once, long before the slider is near.
      const inert = document.createElement('template').content.ownerDocument;
      const keep = selector => this.panels.map(panel => {
        const element = panel.querySelector(selector);
        return element ? inert.importNode(element, true) : null;
      });
      this.pristine = keep('[data-st-scene]');
      // Artwork a reel plays beside its screen, outside the screen's clip (How it works' At home card).
      this.overlays = keep('[data-st-overlay]');
      this.auto = tabs.hasAttribute('data-st-reels-autoplay') && !window.Shopify?.designMode;
      this.resume = this.auto;
      this.current = Math.max(0, this.panels.findIndex(panel => !panel.hidden));
      this.inView = false;
      this.stopped = false;
      // Until it comes into view the screen waits on its first frame, so a visitor never sees
      // the finished screen blank out as its reel begins.
      if (!reducedMotion()) this.mount(this.current, true);
      // The phone panel, not the whole slider: on phones the feature list below it makes the
      // slider far taller than the screen, so a share of the slider never comes into view.
      this.visual = tabs.querySelector('.st-app-visual') || tabs;
      this.observer = new IntersectionObserver(entries => {
        const entry = entries[entries.length - 1];
        if (!entry.isIntersecting) this.rest();
        const visible = entry.isIntersecting && entry.intersectionRatio >= .35;
        if (visible === this.inView) return;
        this.inView = visible;
        if (visible) this.play(this.current); else this.stop();
      }, { threshold: [0, .35] });
      this.observer.observe(this.visual);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.rest(); else if (this.inView) this.play(this.current);
      }, { signal });
      motionPreference.addEventListener('change', () => this.play(this.current), { signal });
      // A click on the phone stops the reels, each screen on its last frame, and another starts
      // them again. A swipe between screens, or a press on the dots, is not that click.
      let press;
      this.visual.addEventListener('pointerdown', event => { press = { x: event.clientX, y: event.clientY }; }, { signal });
      this.visual.addEventListener('click', event => {
        if (event.target.closest('button, a, [data-st-dots]')) return;
        if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) return;
        this.toggle();
      }, { signal });
      // The same switch for keyboards and screen readers. It shows while it has focus.
      this.switch = document.createElement('button');
      this.switch.type = 'button';
      this.switch.className = 'st-app-reels-switch';
      this.switch.textContent = 'Stop animation';
      this.switch.addEventListener('click', () => this.toggle(), { signal });
      this.visual.prepend(this.switch);
      this.visual.classList.add('is-reels');
    }
    // The selection changed in AppTabs. A visitor's choice ends the automatic advance.
    select(index, source) {
      if (source !== 'auto' && source !== 'init') this.stopAuto();
      const previous = this.current;
      this.current = index;
      this.play(index);
      if (previous === index) return;
      // The screen that left goes back to its still state once its dissolve is over.
      clearTimeout(this.resetTimer);
      const reset = () => this.panels.forEach((panel, i) => { if (i !== this.current) this.mount(i, false); });
      if (reducedMotion()) reset(); else this.resetTimer = setTimeout(reset, 350);
    }
    stopAuto() {
      this.auto = this.resume = false;
      this.progress(-1);
    }
    // Stopped, every screen shows its last frame and nothing moves on by itself. Started again,
    // the current reel plays from the beginning and the advance carries on if it was running.
    toggle() {
      if (reducedMotion()) return;
      this.stopped = !this.stopped;
      if (this.stopped) {
        this.resume = this.auto;
        this.auto = false;
      } else this.auto = this.resume;
      this.switch.textContent = this.stopped ? 'Start animation' : 'Stop animation';
      this.tabs.classList.toggle('is-stopped', this.stopped);
      this.play(this.current);
    }
    progress(index, duration = 0) {
      this.buttons.forEach(button => button.querySelector('.st-app-choice__progress')?.getAnimations().forEach(animation => animation.cancel()));
      const bar = this.buttons[index]?.querySelector('.st-app-choice__progress');
      if (bar && duration && this.auto) bar.animate([{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }], { duration, easing: 'linear', fill: 'forwards' });
    }
    mount(index, pending) {
      const panel = this.panels[index];
      const current = panel?.querySelector('[data-st-scene]');
      if (!current || !this.pristine[index]) return null;
      const fresh = document.importNode(this.pristine[index], true);
      fresh.classList.toggle('is-pending', pending);
      current.replaceWith(fresh);
      const overlay = panel.querySelector('[data-st-overlay]');
      if (overlay && this.overlays[index]) {
        const copy = document.importNode(this.overlays[index], true);
        copy.classList.toggle('is-pending', pending);
        overlay.replaceWith(copy);
      }
      const finger = panel.querySelector('.st-phone__finger');
      if (finger) {
        finger.getAnimations().forEach(animation => animation.cancel());
        delete finger.dataset.at;
        panel.querySelectorAll('.st-phone__ripple').forEach(ripple => ripple.remove());
      }
      return fresh;
    }
    stop() {
      this.run?.abort();
      this.progress(-1);
    }
    // Out of sight, the screen goes back to its first frame, ready to play on the next arrival.
    rest() {
      this.stop();
      if (!reducedMotion() && !this.stopped) this.mount(this.current, true);
    }
    async play(index) {
      this.run?.abort();
      const run = this.run = new AbortController();
      // Reduced motion shows each screen as Figma has it and a stopped reel its last frame.
      // Out of view a screen waits on its first frame.
      const mode = reducedMotion() ? 'still' : this.stopped ? 'end' : this.inView ? 'play' : null;
      const screen = this.mount(index, mode === 'play' || !mode);
      if (mode !== 'play') this.progress(-1);
      if (!mode) return;
      // Pages may add scenes of their own (st-app-reels-hiw.js), looked up as each reel starts.
      const reel = screen && (reels[screen.dataset.stScene] || window.SentaiAppReelScenes?.[screen.dataset.stScene]);
      const wait = ms => new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, ms);
        run.signal.addEventListener('abort', () => { clearTimeout(timer); reject(new Cancelled()); }, { once: true });
      });
      try {
        if (!reel) {
          // A screenshot panel: let it be seen, then move on.
          if (mode !== 'play' || !this.auto) return;
          this.progress(index, IMAGE_MS);
          await wait(IMAGE_MS);
        } else {
          if (mode === 'play') this.progress(index, reel.length + HOLD_MS);
          const started = performance.now();
          await reel.play(director(screen, run.signal, mode));
          if (mode !== 'play') return;
          await wait(Math.max(HOLD_MS, reel.length + HOLD_MS - (performance.now() - started)));
        }
        if (this.auto) this.tabs.selectFeature((index + 1) % this.panels.length);
        else if (reel) {
          // Fade the finished screen away and play it again.
          await director(screen, run.signal, 'play').play([screen, screen.closest('.st-phone')?.querySelector('[data-st-overlay]')], [{ opacity: 1 }, { opacity: 0 }], { duration: 400 });
          this.play(index);
        }
      } catch (error) {
        if (!(error instanceof Cancelled)) throw error;
      }
    }
    destroy() {
      clearTimeout(this.resetTimer);
      this.stop();
      this.abort.abort();
      this.observer.disconnect();
      this.switch.remove();
      this.visual.classList.remove('is-reels');
      this.tabs.classList.remove('is-stopped');
    }
  }

  window.SentaiAppReels = {
    attach(tabs) {
      if (tabs.reels || !tabs.selectFeature || !tabs.querySelector('[data-st-scene]') || !('IntersectionObserver' in window) || !CSS.supports('width: 1cqi')) return;
      tabs.reels = new AppReels(tabs);
    }
  };
  // st-theme.js usually connects the sliders first; it attaches any that connect later.
  document.querySelectorAll('st-app-tabs').forEach(tabs => window.SentaiAppReels.attach(tabs));
})();
